import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Share } from 'lucide-react';

/**
 * InstallPrompt Component
 * Shows a banner prompting users to install the PWA
 * Handles both Android/Chrome (native prompt) and iOS (manual instructions)
 */
export function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [showIOSPrompt, setShowIOSPrompt] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);

    // Expose install function globally for manual trigger
    useEffect(() => {
        const handleManualInstall = async () => {
            console.log('[PWA] Manual install triggered');
            if (deferredPrompt) {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                console.log(`[PWA] Manual install outcome: ${outcome}`);
                setDeferredPrompt(null);
                setShowPrompt(false);
            } else {
                console.log('[PWA] No deferred prompt available for manual install');
                // Could explain to user why
            }
        };

        window.addEventListener('pwa-install-trigger', handleManualInstall);
        return () => window.removeEventListener('pwa-install-trigger', handleManualInstall);
    }, [deferredPrompt]);


    // Check if already installed
    useEffect(() => {
        const checkInstalled = () => {
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
            const isIOSStandalone = window.navigator.standalone === true;
            setIsInstalled(isStandalone || isIOSStandalone);
        };

        checkInstalled();

        // Listen for display mode changes
        const mediaQuery = window.matchMedia('(display-mode: standalone)');
        mediaQuery.addEventListener('change', checkInstalled);

        return () => mediaQuery.removeEventListener('change', checkInstalled);
    }, []);

    // Listen for beforeinstallprompt event (Chrome/Android)
    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            console.log('[PWA] beforeinstallprompt fired');
            // Prevent the mini-infobar from appearing
            e.preventDefault();
            // Store the event for later use
            setDeferredPrompt(e);

            // Dispatch event to let app know install is possible
            window.dispatchEvent(new CustomEvent('pwa-install-available', { detail: { available: true } }));

            // Check if user has dismissed recently
            const dismissedAt = localStorage.getItem('pwa-install-dismissed');
            if (dismissedAt) {
                const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
                if (daysSinceDismissed < 7) {
                    console.log('[PWA] Automatic prompt suppressed (dismissed recently)');
                    return; // Don't show for 7 days after dismissal
                }
            }

            console.log('[PWA] Showing automatic prompt');
            setShowPrompt(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    // Check for iOS
    useEffect(() => {
        const ua = navigator.userAgent;
        const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        // We allow Chrome on iOS too as modern versions support adding to home screen
        // const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);

        console.log('[PWA] iOS Check:', { isIOS, isInstalled, ua });

        if (isIOS && !isInstalled) {
            // Check if user has dismissed recently
            const dismissedAt = localStorage.getItem('pwa-install-dismissed');
            if (dismissedAt) {
                const daysSinceDismissed = (Number(Date.now()) - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
                if (daysSinceDismissed < 7) {
                    console.log('[PWA] iOS prompt dismissed recently');
                    return;
                }
            }

            // Show iOS prompt after a short delay
            const timer = setTimeout(() => {
                setShowIOSPrompt(true);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [isInstalled]);

    // Handle install button click
    const handleInstall = useCallback(async () => {
        if (!deferredPrompt) return;

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user's response
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to install prompt: ${outcome}`);

        // Clear the deferred prompt
        setDeferredPrompt(null);
        setShowPrompt(false);
    }, [deferredPrompt]);

    // Handle dismiss
    const handleDismiss = useCallback(() => {
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
        setShowPrompt(false);
        setShowIOSPrompt(false);
    }, []);

    // Don't render if already installed
    if (isInstalled) return null;

    // iOS Safari prompt
    if (showIOSPrompt) {
        return (
            <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-300">
                <div className="max-w-md mx-auto bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-4">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                            <Share className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white text-sm">Install ClinicNode</h3>
                            <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                                Tap <Share className="w-3 h-3 inline-block mx-0.5" /> then <strong>&quot;Add to Home Screen&quot;</strong> for the best experience.
                            </p>
                        </div>
                        <button
                            onClick={handleDismiss}
                            className="flex-shrink-0 text-slate-500 hover:text-slate-300 transition-colors p-1"
                            aria-label="Dismiss"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Chrome/Android install prompt
    if (!showPrompt) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-300">
            <div className="max-w-md mx-auto bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-4">
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                        <img
                            src="/icon-192.png"
                            alt="ClinicNode"
                            className="w-12 h-12 rounded-lg"
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white text-sm">Install ClinicNode</h3>
                        <p className="text-slate-400 text-xs mt-0.5">
                            Add to your home screen for quick access
                        </p>
                        <div className="flex gap-2 mt-3">
                            <Button
                                size="sm"
                                onClick={handleInstall}
                                className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-medium text-xs px-3 h-8"
                            >
                                <Download className="w-3.5 h-3.5 mr-1.5" />
                                Install
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleDismiss}
                                className="text-slate-400 hover:text-slate-300 text-xs px-3 h-8"
                            >
                                Not now
                            </Button>
                        </div>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="flex-shrink-0 text-slate-500 hover:text-slate-300 transition-colors p-1"
                        aria-label="Dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default InstallPrompt;
